/**
 * Daily Readings + Tank Inventory — single guided workflow.
 * Version 1.1 · Sprint 2. PO directive 2026-07-31.
 *
 * Two collapsible sections on one page. Auto-save on blur (every edit
 * persists), separate "Mark Complete" per section, live subtotals.
 * Header: "Daily Readings for [yesterday] · Preparing operations for [today]".
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import {
  CaretDown, CaretUp, CheckCircle, Warning, CircleNotch,
  Gauge, Package as TankIcon, Truck as TruckIcon,
} from "@phosphor-icons/react";
import { api } from "@/lib/api";

const PRODUCT_LABEL = {
  regular: "Regular",
  premium: "Premium",
  clear_diesel: "Clear Diesel",
  dyed_diesel: "Dyed Diesel",
  def: "DEF",
};

const TRUCK_PRODUCTS = ["regular", "premium", "clear_diesel", "dyed_diesel"];

export default function StartTheDayCapture({ initialSection }) {
  const nav = useNavigate();
  const location = useLocation();
  const forcedSection = initialSection
    || (location.pathname.endsWith("tank-inventory") ? "tank" : "daily");

  const [daily,     setDaily]     = useState(null);
  const [tank,      setTank]      = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [openSec,   setOpenSec]   = useState(forcedSection);
  const [saving,    setSaving]    = useState({ daily: false, tank: false });
  const [dirty,     setDirty]     = useState({ daily: false, tank: false });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: d }, { data: t }] = await Promise.all([
        api.get("/morning-operations/daily-readings"),
        api.get("/morning-operations/tank-inventory"),
      ]);
      setDaily(d);
      setTank(t);
    } catch { toast.error("Could not load readings"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  // ---- Auto-save helpers ---------------------------------------------------
  const saveDaily = async (patch) => {
    setSaving((s) => ({ ...s, daily: true }));
    try {
      const { data } = await api.post("/morning-operations/daily-readings", patch);
      setDaily(data);
      setDirty((d) => ({ ...d, daily: false }));
    } catch { toast.error("Save failed"); }
    finally { setSaving((s) => ({ ...s, daily: false })); }
  };
  const saveTank = async (patch) => {
    setSaving((s) => ({ ...s, tank: true }));
    try {
      const { data } = await api.post("/morning-operations/tank-inventory", patch);
      setTank(data);
      setDirty((d) => ({ ...d, tank: false }));
    } catch { toast.error("Save failed"); }
    finally { setSaving((s) => ({ ...s, tank: false })); }
  };

  // ---- Completion handlers -------------------------------------------------
  const complete = async (which) => {
    try {
      const path = which === "daily" ? "/morning-operations/daily-readings/complete"
                                     : "/morning-operations/tank-inventory/complete";
      await api.post(path, {});
      toast.success(`${which === "daily" ? "Daily Readings" : "Tank Inventory"} marked complete`);
      load();
      // Bounce back to Start the Day so the operator sees progress.
      nav("/start-the-day");
    } catch { toast.error("Could not mark complete"); }
  };

  if (loading) return <div className="text-sm text-ink-secondary" data-testid="std-capture-loading">Loading…</div>;

  const headerLine = daily
    ? `Daily Readings for ${formatDate(daily.brief_date)} · Preparing operations for ${formatDate(daily.operations_for_date)}`
    : "";

  return (
    <div className="mx-auto max-w-4xl space-y-6" data-testid="std-capture-page">
      <header>
        <h1 className="font-display text-2xl font-semibold text-ink-primary">Morning Operations</h1>
        <p className="text-sm text-ink-secondary" data-testid="std-capture-header">{headerLine}</p>
      </header>

      {/* Section 1 · Daily Readings */}
      <Section
        title="Daily Readings"
        subtitle="Cardlock meters · Loading rack meters · Credit Card Skimming Inspection"
        icon={<Gauge size={20} weight="fill" />}
        open={openSec === "daily"}
        onToggle={() => setOpenSec(openSec === "daily" ? null : "daily")}
        status={daily?.status}
        saving={saving.daily}
        dirty={dirty.daily}
        testid="section-daily"
      >
        <DailyReadings
          data={daily}
          onSave={saveDaily}
          onDirty={() => setDirty((d) => ({ ...d, daily: true }))}
        />
        <SectionActions
          canComplete={daily?.status !== "complete"}
          statusLabel={
            daily?.status === "complete"
              ? `Completed by ${daily.completed_by || "—"} · ${daily.completed_at ? new Date(daily.completed_at).toLocaleString() : ""}`
              : "Auto-saved. Ready when you are."
          }
          onComplete={() => complete("daily")}
          testid="daily-actions"
        />
      </Section>

      {/* Section 2 · Tank Inventory */}
      <Section
        title="Tank Inventory"
        subtitle="Enter today's closing physical — opening balances carry forward automatically"
        icon={<TankIcon size={20} weight="fill" />}
        open={openSec === "tank"}
        onToggle={() => setOpenSec(openSec === "tank" ? null : "tank")}
        status={tank?.status}
        saving={saving.tank}
        dirty={dirty.tank}
        testid="section-tank"
      >
        <TankInventory
          data={tank}
          onSave={saveTank}
          onDirty={() => setDirty((d) => ({ ...d, tank: true }))}
        />
        <SectionActions
          canComplete={tank?.status !== "complete"}
          statusLabel={
            tank?.status === "complete"
              ? `Completed by ${tank.completed_by || "—"} · ${tank.completed_at ? new Date(tank.completed_at).toLocaleString() : ""}`
              : "Auto-saved. Ready when you are."
          }
          onComplete={() => complete("tank")}
          testid="tank-actions"
        />
      </Section>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Section shell
// -----------------------------------------------------------------------------
function Section({ title, subtitle, icon, open, onToggle, status, saving, dirty, testid, children }) {
  const tone = status === "complete" ? "text-emerald-600" : "text-amber-600";
  const badge = status === "complete" ? "Complete" : "In progress";
  return (
    <section className="rounded-lg border border-divider bg-surface" data-testid={testid}>
      <button
        onClick={onToggle}
        className="flex w-full items-center gap-3 px-5 py-4 text-left"
        data-testid={`${testid}-toggle`}
      >
        <div className="rounded-md bg-brand/10 p-2 text-brand">{icon}</div>
        <div className="flex-1">
          <div className="font-semibold text-ink-primary">{title}</div>
          <div className="text-[11px] text-ink-secondary">{subtitle}</div>
        </div>
        <div className="flex items-center gap-2">
          {saving && <CircleNotch size={14} className="animate-spin text-ink-muted" />}
          {!saving && dirty && <span className="text-[11px] text-ink-muted">Unsaved</span>}
          <span className={`text-[11px] font-semibold uppercase tracking-wide ${tone}`}>{badge}</span>
          {open ? <CaretUp size={14} /> : <CaretDown size={14} />}
        </div>
      </button>
      {open && <div className="border-t border-divider p-5">{children}</div>}
    </section>
  );
}

function SectionActions({ statusLabel, onComplete, canComplete, testid }) {
  return (
    <div className="mt-5 flex flex-wrap items-center justify-between gap-3 border-t border-divider pt-4" data-testid={testid}>
      <div className="text-xs text-ink-secondary" data-testid={`${testid}-status`}>{statusLabel}</div>
      <button
        onClick={onComplete}
        disabled={!canComplete}
        data-testid={`${testid}-mark-complete`}
        className="inline-flex items-center gap-2 rounded-md bg-brand px-4 py-2 text-sm font-semibold text-brand-foreground hover:bg-brand-hover disabled:opacity-50"
      >
        <CheckCircle size={14} weight="bold" /> Mark Complete
      </button>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Daily Readings body
// -----------------------------------------------------------------------------
function DailyReadings({ data, onSave, onDirty }) {
  const cardlockSubtotals = useMemo(() => aggregateThroughput(data?.cardlock_pumps), [data]);
  const rackSubtotals     = useMemo(() => aggregateThroughput(data?.loading_rack),   [data]);

  const commitField = (which, key, value) => {
    if (which === "cardlock") {
      const payload = (data?.cardlock_pumps || []).map((r) =>
        r.pump === key ? { pump: r.pump, close: value } : { pump: r.pump, close: r.close }
      );
      onSave({ cardlock_pumps: payload });
    } else if (which === "rack") {
      const payload = (data?.loading_rack || []).map((r) =>
        r.product === key ? { product: r.product, close: value } : { product: r.product, close: r.close }
      );
      onSave({ loading_rack: payload });
    }
  };

  return (
    <div className="space-y-6">
      {/* Cardlock */}
      <div>
        <h3 className="mb-2 font-display text-sm font-semibold uppercase tracking-wide text-ink-secondary">
          Cardlock Meters
        </h3>
        <MetersTable
          rows={data?.cardlock_pumps || []}
          keyField="pump"
          renderKey={(r) => `Pump #${r.pump}`}
          renderProduct={(r) => PRODUCT_LABEL[r.product] || r.product}
          onBlur={(row, val) => commitField("cardlock", row.pump, val)}
          onDirty={onDirty}
          testidPrefix="cl"
        />
        <Subtotals subtotals={cardlockSubtotals} testidPrefix="cl-sub" />
      </div>
      {/* Loading Rack */}
      <div>
        <h3 className="mb-2 font-display text-sm font-semibold uppercase tracking-wide text-ink-secondary">
          Loading Rack Meters
        </h3>
        <MetersTable
          rows={data?.loading_rack || []}
          keyField="product"
          renderKey={(r) => PRODUCT_LABEL[r.product] || r.product}
          renderProduct={() => ""}
          onBlur={(row, val) => commitField("rack", row.product, val)}
          onDirty={onDirty}
          testidPrefix="lr"
        />
        <Subtotals subtotals={rackSubtotals} testidPrefix="lr-sub" />
      </div>
      {/* Skimming */}
      <SkimmingBlock
        value={data?.skimming_inspection}
        onSave={(patch) => onSave({ skimming_inspection: patch })}
      />
    </div>
  );
}

function MetersTable({ rows, keyField, renderKey, renderProduct, onBlur, onDirty, testidPrefix }) {
  return (
    <table className="w-full text-sm" data-testid={`${testidPrefix}-table`}>
      <thead className="border-b border-divider text-left text-[11px] uppercase tracking-wide text-ink-secondary">
        <tr>
          <th className="py-2 pr-3">Meter</th>
          <th className="py-2 pr-3">Product</th>
          <th className="py-2 pr-3 text-right">Open</th>
          <th className="py-2 pr-3 text-right">Close</th>
          <th className="py-2 pr-3 text-right">Throughput</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r[keyField]} className="border-b border-divider last:border-b-0"
              data-testid={`${testidPrefix}-row-${r[keyField]}`}>
            <td className="py-2 pr-3 font-semibold text-ink-primary">{renderKey(r)}</td>
            <td className="py-2 pr-3 text-ink-secondary">{renderProduct(r)}</td>
            <td className="py-2 pr-3 text-right font-mono text-ink-secondary">{r.open ?? "—"}</td>
            <td className="py-2 pr-3 text-right">
              <input
                defaultValue={r.close ?? ""}
                onChange={onDirty}
                onBlur={(e) => onBlur(r, e.target.value)}
                inputMode="decimal"
                className="w-32 rounded-md border border-divider bg-surface px-2 py-1 text-right font-mono focus:border-brand focus:outline-none"
                data-testid={`${testidPrefix}-close-${r[keyField]}`}
              />
            </td>
            <td className="py-2 pr-3 text-right font-mono text-ink-primary"
                data-testid={`${testidPrefix}-tp-${r[keyField]}`}>
              {r.throughput ?? "—"}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function Subtotals({ subtotals, testidPrefix }) {
  const entries = Object.entries(subtotals);
  if (!entries.length) return null;
  return (
    <div className="mt-3 flex flex-wrap gap-3 border-t border-dashed border-divider pt-3 text-xs">
      {entries.map(([k, v]) => (
        <div key={k} className="rounded-md bg-surface-alt px-2 py-1" data-testid={`${testidPrefix}-${k}`}>
          <span className="font-semibold text-ink-primary">{PRODUCT_LABEL[k] || k}</span>
          <span className="ml-2 font-mono text-ink-primary">{v}</span>
          <span className="ml-1 text-ink-secondary">L</span>
        </div>
      ))}
    </div>
  );
}

function SkimmingBlock({ value, onSave }) {
  const [completed, setCompleted] = useState(value?.completed ?? null);
  const [notes, setNotes] = useState(value?.notes ?? "");
  useEffect(() => { setCompleted(value?.completed ?? null); setNotes(value?.notes ?? ""); }, [value]);

  const commit = (nextCompleted, nextNotes) => {
    onSave({ completed: nextCompleted, notes: nextNotes });
  };

  return (
    <div>
      <h3 className="mb-2 font-display text-sm font-semibold uppercase tracking-wide text-ink-secondary">
        Credit Card Skimming Inspection
      </h3>
      <div className="flex flex-wrap items-center gap-3 rounded-md border border-divider bg-surface-alt p-3">
        <label className="inline-flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={completed === true}
            onChange={(e) => { setCompleted(e.target.checked); commit(e.target.checked, notes); }}
            data-testid="skimming-completed"
          />
          Inspection Completed
        </label>
        <input
          type="text"
          placeholder="Notes"
          defaultValue={notes}
          onBlur={(e) => { setNotes(e.target.value); commit(completed, e.target.value); }}
          className="flex-1 rounded-md border border-divider bg-surface px-2 py-1 text-sm focus:border-brand focus:outline-none"
          data-testid="skimming-notes"
        />
        {value?.recorded_by && (
          <div className="text-[11px] text-ink-secondary" data-testid="skimming-audit">
            Last recorded by {value.recorded_by}
            {value.recorded_at && ` at ${new Date(value.recorded_at).toLocaleTimeString()}`}
          </div>
        )}
      </div>
    </div>
  );
}

function aggregateThroughput(rows) {
  const out = {};
  (rows || []).forEach((r) => {
    const v = Number(r.throughput);
    if (!Number.isFinite(v)) return;
    out[r.product] = (out[r.product] || 0) + v;
  });
  return Object.fromEntries(Object.entries(out).map(([k, v]) => [k, v.toLocaleString(undefined, { maximumFractionDigits: 2 })]));
}

// -----------------------------------------------------------------------------
// Tank Inventory body
// -----------------------------------------------------------------------------
function TankInventory({ data, onSave, onDirty }) {
  const bulk = data?.bulk_plant || [];
  const trucks = data?.trucks || [];
  const productTotals = data?.product_totals || [];

  const truckSubtotals = useMemo(() => {
    const out = {};
    trucks.forEach((r) => {
      TRUCK_PRODUCTS.forEach((p) => {
        const v = Number(r[p]);
        if (!Number.isFinite(v)) return;
        out[p] = (out[p] || 0) + v;
      });
    });
    return out;
  }, [trucks]);

  const commitBulk = (tank_id, patch) => {
    // patch is a partial like { closing_physical } or { opening_balance, opening_notes }.
    // Every row in payload needs at least tank_id — untouched rows go through
    // untouched so the server's merge-preserving logic keeps their data.
    const payload = bulk.map((r) => {
      if (r.tank_id !== tank_id) return { tank_id: r.tank_id };
      return { tank_id: r.tank_id, ...patch };
    });
    onSave({ bulk_plant: payload });
  };
  const commitTruck = (truck_id, patch) => {
    const payload = trucks.map((r) => {
      if (r.truck_id !== truck_id) {
        return { truck_id: r.truck_id, ...pickProducts(r) };
      }
      return { truck_id: r.truck_id, ...pickProducts(r), ...patch };
    });
    onSave({ trucks: payload });
  };

  return (
    <div className="space-y-6">
      {/* Bulk Plant */}
      <div>
        <h3 className="mb-2 font-display text-sm font-semibold uppercase tracking-wide text-ink-secondary">
          Bulk Plant · Enter Today's Closing Physical
        </h3>
        <p className="mb-3 text-xs text-ink-secondary">
          Opening balances are auto-carried from yesterday's closing. Use <b>Override</b> only when a manual correction is required.
        </p>
        <table className="w-full text-sm" data-testid="bulk-plant-table">
          <thead className="border-b border-divider text-left text-[11px] uppercase tracking-wide text-ink-secondary">
            <tr>
              <th className="py-2 pr-3">Tank</th>
              <th className="py-2 pr-3">Product</th>
              <th className="py-2 pr-3">Location</th>
              <th className="py-2 pr-3 text-right">Capacity (L)</th>
              <th className="py-2 pr-3 text-right">Opening (L) · reference</th>
              <th className="py-2 pr-3 text-right text-brand font-semibold">Today's Closing (L)</th>
              <th className="py-2 pr-3 text-right">Throughput (L)</th>
            </tr>
          </thead>
          <tbody>
            {bulk.map((t) => (
              <BulkPlantRow
                key={t.tank_id}
                t={t}
                onDirty={onDirty}
                onOpeningChange={(val, notes) =>
                  commitBulk(t.tank_id, { opening_balance: val, opening_notes: notes })
                }
                onClosingChange={(val) =>
                  commitBulk(t.tank_id, { closing_physical: val })
                }
              />
            ))}
          </tbody>
        </table>

        {/* Product totals summary — bottom of Bulk Plant section */}
        {productTotals.length > 0 && (
          <div
            data-testid="bp-product-totals"
            className="mt-4 grid gap-3 border-t border-dashed border-divider pt-4 sm:grid-cols-2 lg:grid-cols-4"
          >
            {productTotals.map((pt) => (
              <div
                key={pt.product}
                data-testid={`bp-total-${pt.product}`}
                className="rounded-md border border-divider bg-surface-alt px-3 py-2"
              >
                <div className="text-xs font-semibold uppercase tracking-wider text-ink-secondary">
                  {PRODUCT_LABEL[pt.product] || pt.product}
                </div>
                <div className="mt-1 space-y-0.5 text-xs">
                  <div className="flex items-baseline justify-between">
                    <span className="text-ink-secondary">Opening</span>
                    <span data-testid={`bp-total-${pt.product}-opening`} className="font-mono text-ink-primary">
                      {formatL(pt.opening)}
                    </span>
                  </div>
                  <div className="flex items-baseline justify-between">
                    <span className="text-ink-secondary">Closing</span>
                    <span data-testid={`bp-total-${pt.product}-closing`} className="font-mono text-ink-primary">
                      {formatL(pt.closing)}
                    </span>
                  </div>
                  <div className="flex items-baseline justify-between border-t border-divider pt-0.5">
                    <span className="text-ink-secondary">Throughput</span>
                    <span data-testid={`bp-total-${pt.product}-throughput`} className="font-mono font-semibold text-ink-primary">
                      {formatL(pt.throughput)}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Trucks */}
      <div>
        <h3 className="mb-2 font-display text-sm font-semibold uppercase tracking-wide text-ink-secondary">
          Trucks · from Fleet
        </h3>
        <table className="w-full text-sm" data-testid="trucks-table">
          <thead className="border-b border-divider text-left text-[11px] uppercase tracking-wide text-ink-secondary">
            <tr>
              <th className="py-2 pr-3">Truck</th>
              <th className="py-2 pr-3">Driver</th>
              {TRUCK_PRODUCTS.map((p) => (
                <th key={p} className="py-2 pr-3 text-right">{PRODUCT_LABEL[p]} (L)</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {trucks.map((tr) => (
              <tr key={tr.truck_id} className="border-b border-divider last:border-b-0"
                  data-testid={`truck-row-${tr.truck_number}`}>
                <td className="py-2 pr-3 font-semibold text-ink-primary">Truck {tr.truck_number}</td>
                <td className="py-2 pr-3 text-ink-secondary">{tr.driver_name || "—"}</td>
                {TRUCK_PRODUCTS.map((p) => (
                  <td key={p} className="py-2 pr-3 text-right">
                    <input
                      defaultValue={tr[p] ?? ""}
                      onChange={onDirty}
                      onBlur={(e) => commitTruck(tr.truck_id, { [p]: e.target.value })}
                      inputMode="decimal"
                      className="w-28 rounded-md border border-divider bg-surface px-2 py-1 text-right font-mono focus:border-brand focus:outline-none"
                      data-testid={`truck-${tr.truck_number}-${p}`}
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        <ProductRow subtotals={truckSubtotals} testidPrefix="truck-sub" />
      </div>
    </div>
  );
}

function pickProducts(row) {
  const out = {};
  TRUCK_PRODUCTS.forEach((p) => { if (row[p] != null) out[p] = row[p]; });
  return out;
}

function ProductRow({ subtotals, testidPrefix }) {
  const entries = Object.entries(subtotals);
  if (!entries.length) return null;
  return (
    <div className="mt-3 flex flex-wrap gap-3 border-t border-dashed border-divider pt-3 text-xs">
      {entries.map(([k, v]) => (
        <div key={k} className="rounded-md bg-surface-alt px-2 py-1" data-testid={`${testidPrefix}-${k}`}>
          <span className="font-semibold text-ink-primary">{PRODUCT_LABEL[k] || k}</span>
          <span className="ml-2 font-mono text-ink-primary">
            {v.toLocaleString(undefined, { maximumFractionDigits: 2 })}
          </span>
          <span className="ml-1 text-ink-secondary">L</span>
        </div>
      ))}
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return "";
  try {
    // Sprint 10 · 2026-08-21 · The backend emits date-only strings
    // ("2026-08-20"). Build LOCAL midnight, not UTC midnight, so that
    // toLocaleDateString never rolls the date back a day for viewers
    // west of UTC (Ontario is EDT = UTC-4; UTC midnight is 8pm EDT
    // the previous day → "Wed Aug 19" instead of "Thu Aug 20").
    const [y, m, d] = iso.split("-").map(Number);
    return new Date(y, m - 1, d).toLocaleDateString(undefined, {
      weekday: "long", month: "long", day: "numeric",
    });
  } catch { return iso; }
}


/**
 * BulkPlantRow — one row of the Bulk Plant tank grid.
 *
 * Opening column shows an editable numeric input plus an "Auto" pill
 * when the value was carried forward from yesterday's closing (source
 * "auto"). Blurring commits the edit as an operator override.
 * Throughput is server-computed and read-only.
 */
/* Bulk-plant tank row.
 *
 * PO directive · 2026-08-14 — the normal Start-the-Day flow only asks
 * the operator for **today's closing physical**. Opening balances are
 * automatically carried forward from yesterday's closing and rendered
 * read-only. The manual-override capability is preserved but tucked
 * behind an explicit *Override* toggle so operators can't fall into it
 * by mistake. Overriding still marks the row `Manual`, records the
 * override notes, and keeps the full audit trail on the server.
 * Throughput is server-computed and read-only.
 */
function BulkPlantRow({ t, onDirty, onOpeningChange, onClosingChange }) {
  const isManual = t.opening_source === "manual";
  const [overriding, setOverriding] = useState(false);
  const openingBaseline = String(t.opening_balance ?? "");
  const closingBaseline = String(t.closing_physical ?? "");
  const commitOpeningIfChanged = (raw, notes) => {
    const next = String(raw ?? "").trim();
    if (next === openingBaseline.trim()) return;  // no-op blur — do nothing
    onOpeningChange(next, notes ?? t.opening_notes);
  };
  const commitClosingIfChanged = (raw) => {
    const next = String(raw ?? "").trim();
    if (next === closingBaseline.trim()) return;  // no-op blur — do nothing
    onClosingChange(next);
  };
  const openingDisplay = t.opening_balance != null && t.opening_balance !== ""
    ? formatL(t.opening_balance)
    : "—";
  return (
    <tr
      className="border-b border-divider last:border-b-0"
      data-testid={`bp-row-${t.tank_number}`}
    >
      <td className="py-2 pr-3 font-semibold text-ink-primary">{t.tank_number}</td>
      <td className="py-2 pr-3 text-ink-secondary">{PRODUCT_LABEL[t.product] || t.product}</td>
      <td className="py-2 pr-3 text-ink-secondary">{t.location}</td>
      <td className="py-2 pr-3 text-right font-mono text-ink-secondary">{t.capacity_l}</td>
      <td className="py-2 pr-3 text-right">
        <div className="flex items-center justify-end gap-1.5">
          {overriding ? (
            <>
              <input
                key={`${t.tank_id}-open-override-${t.opening_balance ?? ""}`}
                defaultValue={t.opening_balance ?? ""}
                onChange={onDirty}
                onBlur={(e) => {
                  commitOpeningIfChanged(e.target.value);
                  setOverriding(false);
                }}
                autoFocus
                inputMode="decimal"
                title="Manual override — value will be marked Manual and audited"
                className="w-32 rounded-md border border-amber-300 bg-amber-50 px-2 py-1 text-right font-mono focus:border-amber-500 focus:outline-none"
                data-testid={`bp-opening-${t.tank_number}`}
              />
              <button
                type="button"
                onClick={() => setOverriding(false)}
                data-testid={`bp-opening-cancel-${t.tank_number}`}
                className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 hover:text-slate-700"
                title="Cancel override without changing"
              >
                Cancel
              </button>
            </>
          ) : (
            <>
              <span
                className={`inline-flex min-w-[8rem] justify-end font-mono ${isManual ? "text-amber-900" : "text-ink-primary"}`}
                data-testid={`bp-opening-${t.tank_number}`}
                title={t.opening_source === "auto"
                  ? `Auto-carried from ${t.opening_source_date || "prior day"}`
                  : isManual
                    ? `Manual override by ${t.opening_updated_by || "operator"}${t.opening_notes ? ` · ${t.opening_notes}` : ""}`
                    : "Opening balance"}
              >
                {openingDisplay}
              </span>
              {t.opening_source === "auto" && (
                <span
                  data-testid={`bp-opening-auto-${t.tank_number}`}
                  className="rounded-full bg-slate-100 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-slate-600"
                >
                  Auto
                </span>
              )}
              {isManual && (
                <span
                  data-testid={`bp-opening-manual-${t.tank_number}`}
                  title={t.opening_notes || "Manual override"}
                  className="rounded-full bg-amber-200 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-amber-900"
                >
                  Manual
                </span>
              )}
              <button
                type="button"
                onClick={() => setOverriding(true)}
                data-testid={`bp-opening-override-btn-${t.tank_number}`}
                className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 hover:text-amber-700"
                title="Override the auto-carried opening balance"
              >
                Override
              </button>
            </>
          )}
        </div>
      </td>
      <td className="py-2 pr-3 text-right">
        <input
          key={`${t.tank_id}-close-${t.closing_physical ?? ""}`}
          defaultValue={t.closing_physical ?? ""}
          onChange={onDirty}
          onBlur={(e) => commitClosingIfChanged(e.target.value)}
          inputMode="decimal"
          autoFocus={t.closing_physical == null || t.closing_physical === ""}
          className="w-32 rounded-md border-2 border-brand/40 bg-white px-2 py-1 text-right font-mono focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
          data-testid={`bp-closing-${t.tank_number}`}
          placeholder="Enter"
        />
      </td>
      <td className="py-2 pr-3 text-right">
        <span
          data-testid={`bp-throughput-${t.tank_number}`}
          className="font-mono text-ink-primary"
        >
          {t.throughput != null ? formatL(t.throughput) : "—"}
        </span>
      </td>
    </tr>
  );
}


function formatL(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  return n.toLocaleString(undefined, { maximumFractionDigits: 2 });
}
